/**
 * KnewtonChatDrawer - Chat interface for asking Knewton about players
 * Displays visual player cards that can be added to parlay
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Markdown from 'react-markdown';
import {
  PaperAirplaneIcon,
  SparklesIcon,
  XMarkIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/solid';
import PlayerResultCard from './PlayerResultCard';
import GameResultCard from './GameResultCard';
import api from '../../services/api';
import { useParlay } from '../../contexts/ParlayContext';
import { openCrispChat } from '../../crisp';

// Suggested quick prompts - prioritize emerging streaks and under plays
const QUICK_PROMPTS = [
  { icon: '🌱', text: 'Emerging 2-game streaks', query: 'show me 2-game streaks likely to extend to 3' },
  { icon: '📉', text: 'Under plays (fade streaks)', query: 'which streaks are about to end' },
  { icon: '🔥', text: 'Hot 5+ game streaks', query: 'show me players on 5+ game streaks' },
  { icon: '🎯', text: 'Top predictions tonight', query: 'show me the top predicted players tonight' },
  { icon: '🏀', text: 'Game predictions', query: 'show me game predictions for tonight' },
  { icon: '🏒', text: 'NHL goal scorers', query: 'who is likely to score a goal tonight' },
  { icon: '🏆', text: 'Best upsets to pick', query: 'what are the best upset picks in March Madness this year? show me sleeper teams and Cinderellas' },
  { icon: '🐅', text: 'Paper tigers to fade', query: 'which March Madness teams are paper tigers? high seeds with weak BPI or inflated records' },
];

export default function KnewtonChatDrawer({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { addToParlay, removeFromParlay, isInParlay, parlayCount } = useParlay();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Add initial greeting when first opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        type: 'knewton',
        content: "Hey! I'm Knewton, your AI betting assistant. I specialize in finding emerging streaks (2-game streaks likely to extend) and under plays (streaks about to break). What can I help you find?",
        disclaimer: "Knewton's advice is for informational and entertainment purposes only. If you choose to wager, do so at your own risk.",
        followUps: [
          { icon: '🌱', text: 'Emerging streaks', query: 'show me 2-game streaks likely to extend to 3' },
          { icon: '📉', text: 'Under plays', query: 'which streaks are about to end' },
          { icon: '🔥', text: 'Hot 5+ streaks', query: 'show me players on 5+ game streaks' },
          { icon: '🎯', text: 'Top predictions', query: 'who is predicted to score 20+ points tonight' }
        ],
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  const handleSendMessage = async (messageText) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading) return;

    // Add user message
    const userMessage = {
      type: 'user',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Use api service which handles base URL and auth
      const response = await api.post('/chat', { query: text });
      const data = response.data;

      // Add Knewton response
      const knewtonMessage = {
        type: 'knewton',
        content: data.message || "Here's what I found:",
        players: data.players || [],
        games: data.games || [],
        followUps: data.follow_ups || [],
        timestamp: new Date()
      };
      setMessages(prev => [...prev, knewtonMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        type: 'knewton',
        content: "Sorry, I couldn't process that request. Try asking about player streaks or predictions!",
        isError: true,
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`
        fixed top-0 right-0 h-full w-full sm:w-[420px] z-50
        bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950
        border-l border-purple-500/20
        shadow-2xl shadow-purple-500/10
        flex flex-col
        transform transition-transform duration-300 ease-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700/50 bg-gradient-to-r from-purple-900/30 to-indigo-900/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-white">Knewton</h2>
              <p className="text-xs text-purple-300">AI Parlay Analyst</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Help Button - Opens Crisp Chat */}
            <button
              onClick={openCrispChat}
              className="p-2 rounded-lg hover:bg-gray-800 transition-colors group"
              title="Get Help"
            >
              <QuestionMarkCircleIcon className="w-5 h-5 text-gray-400 group-hover:text-purple-400" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Parlay Summary (if items selected) */}
        {parlayCount > 0 && (
          <div className="p-3 bg-purple-900/20 border-b border-purple-500/20">
            <div className="flex items-center justify-between">
              <span className="text-sm text-purple-300">
                <span className="font-bold text-white">{parlayCount}</span> leg{parlayCount !== 1 ? 's' : ''} in parlay
              </span>
              <a href="/parlay-workshop" className="text-xs text-purple-400 hover:text-purple-300 underline">
                View Parlay
              </a>
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[90%] ${msg.type === 'user' ? 'order-1' : ''}`}>
                {/* Message bubble */}
                <div className={`
                  rounded-2xl px-4 py-2.5
                  ${msg.type === 'user'
                    ? 'bg-purple-600 text-white rounded-br-sm'
                    : msg.isError
                    ? 'bg-red-900/30 text-red-300 border border-red-500/30 rounded-bl-sm'
                    : 'bg-gray-800 text-gray-100 rounded-bl-sm'
                  }
                `}>
                  <div className="text-sm prose prose-invert prose-sm max-w-none
                    prose-headings:text-white prose-headings:font-bold prose-headings:mt-3 prose-headings:mb-1
                    prose-h2:text-base prose-h3:text-sm
                    prose-p:my-1 prose-p:leading-relaxed
                    prose-strong:text-purple-200
                    prose-ul:my-1 prose-li:my-0.5
                    prose-a:text-purple-400 prose-a:no-underline hover:prose-a:underline
                  ">
                    <Markdown>{msg.content}</Markdown>
                  </div>
                  {msg.disclaimer && (
                    <p className="text-xs text-gray-400 italic mt-2 pt-2 border-t border-gray-700/50">
                      {msg.disclaimer}
                    </p>
                  )}
                </div>

                {/* Player cards */}
                {msg.players && msg.players.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {msg.players.map((player, pIdx) => (
                      <PlayerResultCard
                        key={pIdx}
                        player={player}
                        onAdd={() => addToParlay(player)}
                        onRemove={() => removeFromParlay(player)}
                        isInParlay={isInParlay(player)}
                      />
                    ))}
                  </div>
                )}

                {/* Game prediction cards */}
                {msg.games && msg.games.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {msg.games.map((game, gIdx) => (
                      <GameResultCard
                        key={gIdx}
                        game={game}
                      />
                    ))}
                  </div>
                )}

                {/* Follow-up action buttons */}
                {msg.followUps && msg.followUps.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {msg.followUps.map((followUp, fIdx) => (
                      <button
                        key={fIdx}
                        onClick={() => handleSendMessage(followUp.query)}
                        disabled={isLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                          bg-purple-900/40 hover:bg-purple-800/50
                          border border-purple-500/30 hover:border-purple-400/50
                          text-xs text-purple-200 hover:text-white
                          transition-all duration-200
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span>{followUp.icon}</span>
                        <span>{followUp.text}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Timestamp */}
                <p className={`text-[10px] mt-1 ${msg.type === 'user' ? 'text-right text-purple-300' : 'text-gray-500'}`}>
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-800 rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-xs text-gray-400">Knewton is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts */}
        {messages.length <= 1 && (
          <div className="p-3 border-t border-gray-700/50">
            <p className="text-xs text-gray-400 mb-2">Quick prompts:</p>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_PROMPTS.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSendMessage(prompt.query)}
                  disabled={isLoading}
                  className="flex items-center gap-2 p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700/50 hover:border-purple-500/30 transition-all text-left group"
                >
                  <span className="text-lg">{prompt.icon}</span>
                  <span className="text-xs text-gray-300 group-hover:text-white">{prompt.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t border-gray-700/50 bg-gray-900/50">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex items-center gap-2"
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask about streaks, predictions, matchups..."
              disabled={isLoading}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/50 transition-colors"
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="p-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors"
            >
              <PaperAirplaneIcon className="w-5 h-5 text-white" />
            </button>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
}
